import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js';
import {
  createNestablePublicClientApplication,
  type IPublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { getAppConfig } from '@/config/app-config';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt?: number;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
}

interface AuthDialogMessage {
  type: 'auth-success';
  accessToken: string;
  refreshToken: string;
  expiresAt?: number;
  user: AuthUser;
}

let supabaseInstance: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!supabaseInstance) {
    const config = getAppConfig();
    supabaseInstance = createClient(config.supabaseUrl, config.supabaseAnonKey);
  }
  return supabaseInstance;
}

/**
 * Sign in with email and password.
 */
export async function signInWithPassword(
  email: string,
  password: string,
): Promise<{ session: Session; user: AuthUser }> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  if (!data.session) throw new Error('No session returned');

  return {
    session: data.session,
    user: {
      id: data.user.id,
      email: data.user.email ?? '',
      name: data.user.user_metadata?.full_name ?? data.user.email ?? '',
      avatar: data.user.user_metadata?.avatar_url,
    },
  };
}

/**
 * Sign in using Office dialog API for OAuth (Azure SSO).
 * Opens a dialog pointing to auth-dialog.html which handles OAuth.
 */
export async function signInWithOfficeDialog(): Promise<{
  session: Session;
  user: AuthUser;
}> {
  const config = getAppConfig();
  const dialogUrl =
    `${window.location.origin}/auth-dialog.html` +
    `?supabaseUrl=${encodeURIComponent(config.supabaseUrl)}` +
    `&supabaseAnonKey=${encodeURIComponent(config.supabaseAnonKey)}`;

  return new Promise((resolve, reject) => {
    if (typeof Office === 'undefined' || !Office.context?.ui) {
      reject(new Error('Office Dialog API not available'));
      return;
    }

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 40, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Dialog failed: ${result.error.message}`));
          return;
        }

        const dialog = result.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          async (arg: unknown) => {
            const msgArg = arg as { message: string };
            dialog.close();

            try {
              const message: AuthDialogMessage = JSON.parse(msgArg.message);

              if (message.type === 'auth-success') {
                const supabase = getSupabase();
                const { data, error } = await supabase.auth.setSession({
                  access_token: message.accessToken,
                  refresh_token: message.refreshToken,
                });

                if (error) throw error;
                if (!data.session) throw new Error('Failed to establish session');

                resolve({
                  session: data.session,
                  user: message.user,
                });
              } else {
                reject(new Error('Unexpected dialog message'));
              }
            } catch (err) {
              reject(err);
            }
          },
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg: unknown) => {
            const evtArg = arg as { error: number };
            if (evtArg.error === 12006) {
              reject(new Error('Sign in cancelled'));
            }
          },
        );
      },
    );
  });
}

/**
 * Get the current Supabase session (checks stored auth).
 */
export async function getCurrentSession(): Promise<{
  session: Session | null;
  user: User | null;
}> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.getSession();

  if (error || !data.session) {
    return { session: null, user: null };
  }

  return { session: data.session, user: data.session.user };
}

/**
 * Get a valid access token, refreshing if needed.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.getSession();

  if (error || !data.session) return null;

  // Check if token is about to expire (within 60 seconds)
  const expiresAt = data.session.expires_at ?? 0;
  const now = Math.floor(Date.now() / 1000);

  if (expiresAt - now < 60) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed.session) return null;
    return refreshed.session.access_token;
  }

  return data.session.access_token;
}

/**
 * Refresh the current token.
 */
export async function refreshToken(): Promise<string | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session) return null;
  return data.session.access_token;
}

/**
 * Sign out and clear session.
 */
export async function signOut(): Promise<void> {
  const supabase = getSupabase();
  await supabase.auth.signOut();
}

// ---------------------------------------------------------------------------
// NAA (Nested App Authentication) — the modern Office SSO approach.
// Uses MSAL.js with createNestablePublicClientApplication to get an Azure AD
// token silently, then exchanges it for a Supabase session via the backend.
// Requires NestedAppAuth 1.1 requirement set and a simple SPA app registration.
// ---------------------------------------------------------------------------

let msalInstance: IPublicClientApplication | null = null;

async function getMsalInstance(): Promise<IPublicClientApplication> {
  if (!msalInstance) {
    const config = getAppConfig();
    msalInstance = await createNestablePublicClientApplication({
      auth: {
        clientId: config.azureClientId,
        authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
      },
    });
  }
  return msalInstance;
}

/**
 * Get the login hint from Office.auth.getAuthContext().
 * Required for Word/Excel/PowerPoint on the web so MSAL can identify the user.
 */
async function getLoginHint(): Promise<string | null> {
  try {
    if (typeof Office !== 'undefined' && Office.auth && typeof Office.auth.getAuthContext === 'function') {
      const authContext = await Office.auth.getAuthContext();
      if (authContext?.userPrincipalName) return authContext.userPrincipalName;
    }
  } catch (err) {
    console.warn('[auth] Could not get login hint:', err);
  }
  return null;
}

/**
 * Check whether NAA (NestedAppAuth 1.1) is supported in this Office host.
 */
function isNAASupported(): boolean {
  return (
    typeof Office !== 'undefined' &&
    !!Office.context?.requirements?.isSetSupported &&
    Office.context.requirements.isSetSupported('NestedAppAuth', '1.1')
  );
}

/**
 * Sign in silently using Office NAA (Nested App Authentication).
 *
 * Flow:
 *  1. Get login hint via Office.auth.getAuthContext()
 *  2. Use MSAL ssoSilent (or acquireTokenPopup as fallback) to get an Azure AD token
 *  3. Exchange the token for a Supabase session via the backend
 *
 * Falls back to Office.auth.getAccessToken if NAA isn't available but IdentityAPI is.
 */
export async function signInWithOfficeSSOSilent(): Promise<{
  session: Session;
  user: AuthUser;
}> {
  let officeToken: string;

  if (isNAASupported()) {
    // --- NAA path (preferred) ---
    const msal = await getMsalInstance();
    const loginHint = await getLoginHint();

    const request = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      ...(loginHint ? { loginHint } : {}),
    };

    let authResult;
    try {
      authResult = await msal.ssoSilent(request);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Don't auto-popup — let the caller decide whether to show UI
        throw new Error('NAA: silent auth failed, interaction required');
      }
      throw err;
    }

    if (!authResult?.accessToken) {
      throw new Error('NAA: could not acquire access token');
    }
    officeToken = authResult.accessToken;
  } else if (
    typeof Office !== 'undefined' &&
    Office.auth &&
    typeof Office.auth.getAccessToken === 'function'
  ) {
    // --- Legacy IdentityAPI 1.3 path ---
    officeToken = await Office.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false,
      forMSGraphAccess: false,
    });
  } else {
    throw new Error('Office SSO not available (neither NAA nor IdentityAPI supported)');
  }

  // Exchange the Azure AD token for a Supabase session via the backend
  return exchangeOfficeTokenForSession(officeToken);
}

/**
 * Sign in using NAA with interactive fallback (user-initiated, popup allowed).
 */
export async function signInWithNAAInteractive(): Promise<{
  session: Session;
  user: AuthUser;
}> {
  if (!isNAASupported()) {
    throw new Error('NAA not supported');
  }

  const msal = await getMsalInstance();
  const loginHint = await getLoginHint();

  const request = {
    scopes: ['openid', 'profile', 'email', 'User.Read'],
    ...(loginHint ? { loginHint } : {}),
  };

  let authResult;
  try {
    authResult = await msal.ssoSilent(request);
  } catch {
    // Silent failed — use interactive popup (MSAL, not Office dialog)
    authResult = await msal.acquireTokenPopup(request);
  }

  if (!authResult?.accessToken) {
    throw new Error('NAA: could not acquire access token');
  }

  return exchangeOfficeTokenForSession(authResult.accessToken);
}

/**
 * Exchange an Azure AD access token for a Supabase session.
 */
async function exchangeOfficeTokenForSession(officeToken: string): Promise<{
  session: Session;
  user: AuthUser;
}> {
  const config = getAppConfig();
  const response = await fetch(`${config.assistantsBackendUrl}/api/v1/auth/office-sso`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ officeToken }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Office SSO exchange failed: ${body}`);
  }

  const { accessToken, refreshToken: refresh } = await response.json();

  const supabase = getSupabase();
  const { data, error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refresh,
  });

  if (error) throw error;
  if (!data.session) throw new Error('Failed to establish session');

  return {
    session: data.session,
    user: {
      id: data.session.user.id,
      email: data.session.user.email ?? '',
      name: data.session.user.user_metadata?.full_name ?? data.session.user.email ?? '',
      avatar: data.session.user.user_metadata?.avatar_url,
    },
  };
}

/**
 * Sign in using tokens copied from an existing session (e.g., from dev.studio.presidio.ai).
 */
export async function signInWithToken(
  accessToken: string,
  refreshToken: string,
): Promise<{ session: Session; user: AuthUser }> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });

  if (error) throw error;
  if (!data.session) throw new Error('Failed to establish session');

  return {
    session: data.session,
    user: {
      id: data.session.user.id,
      email: data.session.user.email ?? '',
      name: data.session.user.user_metadata?.full_name ?? data.session.user.email ?? '',
      avatar: data.session.user.user_metadata?.avatar_url,
    },
  };
}

export const officeAuthService = {
  signIn: signInWithOfficeDialog,
  signInSilent: signInWithOfficeSSOSilent,
  signInNAAInteractive: signInWithNAAInteractive,
  signInWithPassword,
  signInWithToken,
  getCurrentSession,
  getValidAccessToken,
  refreshToken,
  signOut,
  getSupabase,
};
